import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'
import { ENVIRONMENT, appEnvironment } from 'domains/org/getty/utils/environment'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore, MoodWithStopPointsOfInterest } from '../../../store'

import { moodSchema } from '../../../schemas/mood'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { Input } from 'components/ui/input'
import { MediaField } from 'domains/media/components/media-field'
import { Description, Image, Title } from 'components/ui/list-item-label'

import { LayoutFullWidthPage } from 'layouts/full-width-page'

import { GetMoodRequest } from 'generated/mos/org/getty/audioaccess'
import { Mood } from 'generated/mos/org/getty/audio'
import { UpdateMoodRequest } from 'generated/mos/org/getty/audiomanagement'
import { Language } from 'generated/mos/i18n'
import { Media } from 'generated/mos/media'

import { bffEndpoint } from 'helpers/bff'
import { pick, areObjectsEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags, convertEntityToFormValues } from 'helpers/schema'
import { isEnglish, isDefaultLanguage, parseLanguage } from 'helpers/i18n'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getMoodSubnav } from '../mood-update/subnav'

import { LANGUAGES, LanguageCode } from '../../../constants'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetMoodRequest' | 'gettyUpdateMoodRequest' | 'gettyUpdateMoodReset'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'moodUpdate'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly moodId: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  name: string | undefined;
  description: string | undefined;
  imageAltText: string | undefined;
  creditText: string | undefined;
  audio: Media.Ref | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);
`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const MoodItem = styled.div`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props

  const updateFormBagAudio = (entity: Media.Entity | undefined) => {
    if(formBag) {
      setFormBag({
        ...formBag,
        values: {
          ...formBag.values,
          audio: entity && entity.ref || undefined
        }
      })
    }
  }

  const isDisabled = isEnglish(language)
  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            field="name"
            label="Name"
            isDisabled={isDisabled}
          />
          <Input
            component="textarea"
            label="Description"
            field="description"
            isDisabled={isDisabled}
          />
          <Input
            label="Alternate Text"
            description="Description of what the image contains if it cannot be shown."
            field="imageAltText"
            isDisabled={isDisabled}
          />
          <Input
            label="Credit"
            description="Credit the author of the photo or object."
            field="creditText"
            isDisabled={isDisabled}
          />
          <MediaField
            kind="MEDIA_KIND_AUDIO"
            mediaRef={formBag.values.audio}
            onUpdate={(entity) => updateFormBagAudio(entity)}
            label="Audio"
            language={language}
            isDisabled={isDefaultLanguage(language)}
          />
        </Fieldset>
      </FormData>
    </Panel>
  ) : null
}

const MoodTranslationsView = (props: Props) => {
  const {
    gettyGetMoodRequest ,
    gettyUpdateMoodRequest,
    historyPush,
    projectId,
    toastNotification,
    moodId,
    moodUpdate,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);

  const backLocation = `/project/${projectId}/moods`;

  const moodUpdateData = statusSelector.data(moodUpdate);
  const mood = moodUpdateData && moodUpdateData.mood;

  const hasSubmitted = useRef(false)

  const hostName = bffEndpoint(window.location.hostname)

  // load initial data
  useEffect(() => {
    if (moodUpdate.status === Status.Idle || (mood && Mood.mustRef(mood.ref).id !== moodId)) {
      gettyGetMoodRequest({
        type: GetMoodRequest.refName,
        moodRef: { typename: 'mos.org.getty.audio.Mood', id: moodId },
      });
    }
  })

  useEffect(() => {
    if (!mood || moodUpdate.status !== Status.Ready) return;

    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {
          const audio = moodSchema.audio.read(mood.audio, curr) as Media.Ref | undefined
          const moodValues: Partial<Mood.Entity> = pick({
            ...Mood.defaults,
            ...mood
          }, 'name', 'description', 'imageAltText', 'creditText', 'audio');
          const initialFormValues: FormValues | undefined = mood ?
            convertEntityToFormValues<Mood.Entity>(moodValues, moodSchema, undefined, curr) as FormValues :
            undefined
          return {
            ...acc,
            [curr.languageCode]: createFormBag(initialFormValues)
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }
    if (mood && moodUpdate.status === Status.Ready) {
      const initValues = getInitialValues()
      setInitialValues(initValues)
      setFormBags(initValues)
    }

    if (moodUpdate.status === Status.Ready && hasSubmitted.current) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      hasSubmitted.current = false
    }

  }, [mood, moodUpdate, moodUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const modified = initialValues && formBags && !areObjectsEqual(initialValues, formBags)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true;
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm);
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, mood: MoodWithStopPointsOfInterest) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    // Assemble Payload
    const translatedValues = translateValues<Mood.Entity, FormValues, LanguageCode>(moodSchema, mood, LANGUAGES, formBags)
    // Update mood with changes.
    gettyUpdateMoodRequest({
      ...UpdateMoodRequest.defaults,
      moodRef: Mood.mustRef(mood.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['name', 'description', 'imageAltText', 'creditText', 'audio'],
      },
      name: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('name')
      },
      description: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('description')
      },
      imageAltText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('imageAltText')
      },
      creditText: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('creditText')
      },
      audio: {
        type: "mos.translatable.TranslatableMediaValue",
        translatedValues: translatedValues('audio')
      },
    })

    hasSubmitted.current = true

    return false;
  };

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if(formBags) {
      setFormBags({
        ...formBags,
        [languageCode]: bag
      })
    }
  }
  const title = mood && mood.name && moodSchema.name ? moodSchema.name.read(mood.name) as string : ''

  const moodImage = mood && moodSchema.imageRef.read(mood.imageRef) as Media.Ref
  const moodImageId = moodImage && moodImage.id

  const imagePath = moodImageId ? `${hostName}/images/${moodImageId}.jpg?s=crop&h=92&w=92` : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate mood journey "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getMoodSubnav(projectId, moodId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={title}
        content={() => (
          <StatusGuard status={moodUpdate} mode="form">
            <Scroll>
              <Panels>
                {formBags && LANGUAGES.map((value) => {
                  const languageCode: LanguageCode = value.languageCode as LanguageCode
                  return (
                    <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                  )
                })}
              </Panels>
            </Scroll>
          </StatusGuard>
        )}
        footer={() => formBags && mood && (
          <FooterEditEntity
            entity={moodUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, mood);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const MoodTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'moodUpdate'),
  {
    ...pick(actionCreators, 'gettyGetMoodRequest', 'gettyUpdateMoodRequest', 'gettyUpdateMoodReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(MoodTranslationsView);
